// ─── Role Definitions ────────────────────────────────────────────────────────

export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
};

// Numeric rank — higher = more access. Used for comparison (e.g. canManageUser)
export const ROLE_RANK = {
  owner: 4,
  admin: 3,
  editor: 2,
  viewer: 1,
};

// Human-readable labels and colors for UI display
export const ROLE_META = {
  owner: {
    label: 'Owner',
    color: 'bg-purple-100 text-purple-800',
    description: 'Full access. Can manage all members including admins.',
  },
  admin: {
    label: 'Admin',
    color: 'bg-blue-100 text-blue-800',
    description: 'Can create content and manage viewers, editors, and admins.',
  },
  editor: {
    label: 'Editor',
    color: 'bg-green-100 text-green-800',
    description: 'Can edit existing content but cannot create new campaigns.',
  },
  viewer: {
    label: 'Viewer',
    color: 'bg-gray-100 text-gray-700',
    description: 'View-only access. Can export data but cannot make changes.',
  },
};

// ─── Permission Map ───────────────────────────────────────────────────────────

export const PERMISSIONS = {
  // Viewing
  VIEW_DASHBOARD: ['owner', 'admin', 'editor', 'viewer'],
  EXPORT_DATA: ['owner', 'admin', 'editor', 'viewer'],

  // Editing (existing items only)
  EDIT_NUDGES: ['owner', 'admin', 'editor'],
  EDIT_CAMPAIGNS: ['owner', 'admin', 'editor'],
  EDIT_SETTINGS: ['owner', 'admin', 'editor'],
  EDIT_INTEGRATIONS: ['owner', 'admin', 'editor'],
  EDIT_CHATBOT: ['owner', 'admin', 'editor'],

  // Creating (new items)
  CREATE_NUDGES: ['owner', 'admin'],
  CREATE_CAMPAIGNS: ['owner', 'admin'],
  CREATE_AB_TESTS: ['owner', 'admin'],

  // AI Features
  USE_AI_CHAT: ['owner', 'admin', 'editor'],
  LAUNCH_AI_CAMPAIGN: ['owner', 'admin'],
  USE_AB_TEST_STUDIO: ['owner', 'admin'],

  // Team Management
  VIEW_TEAM: ['owner', 'admin'],
  INVITE_MEMBERS: ['owner', 'admin'],
  REMOVE_MEMBERS: ['owner', 'admin'],
  CHANGE_MEMBER_ROLE: ['owner', 'admin'],
  CREATE_ADMIN: ['owner', 'admin'],
  CREATE_OWNER: ['owner'],
  REMOVE_OWNER: ['owner'],
};

// ─── Utility Functions ────────────────────────────────────────────────────────

/**
 * Check if a user role has a specific permission
 * @param {string} userRole - 'owner' | 'admin' | 'editor' | 'viewer'
 * @param {string} action - key from PERMISSIONS
 * @returns {boolean}
 */
export const hasPermission = (userRole, action) => {
  if (!userRole || !action) return false;
  return PERMISSIONS[action]?.includes(userRole) ?? false;
};

/**
 * Check if actor can manage (edit/remove) a target user based on role hierarchy
 * Owner can manage everyone. Admin can manage everyone except Owner.
 * @param {string} actorRole
 * @param {string} targetRole
 * @returns {boolean}
 */
export const canManageUser = (actorRole, targetRole) => {
  if (actorRole === 'owner') return true;
  if (actorRole === 'admin' && targetRole !== 'owner') return true;
  return false;
};

/**
 * Returns all roles that an actor is allowed to assign
 * Owner can assign any role. Admin can assign all except owner.
 * @param {string} actorRole
 * @returns {string[]}
 */
export const assignableRoles = (actorRole) => {
  if (actorRole === 'owner') return ['owner', 'admin', 'editor', 'viewer'];
  if (actorRole === 'admin') return ['admin', 'editor', 'viewer'];
  return [];
};
